'use client';

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, Card, CardContent, CardHeader, Chip, Typography } from '@mui/material';
import { Download, Edit, Share2, Copy, Repeat, Clock } from 'lucide-react';
import { PageLoader } from '@/components/ui/page-loader';
import { useToast } from '@crm-src/hooks/use-toast';
import { invoicesApi } from '@crm-src/lib/invoices-api';
import { downloadDocumentPdf, fetchDocumentPdf } from '@crm-src/lib/document-pdf';
import { shareDocumentCompanyName, shareDocumentPdfFilename } from '@crm-src/lib/shareDocumentDefaults';
import { ShareDocumentModal } from '@crm-src/components/share-document-modal';
import { PdfReader } from '@/components/pdf/pdf-reader';
import { CreateRecurringScheduleModal } from '@crm-src/components/create-recurring-schedule-modal';
import { BackButton } from "@/components/ui/back-button";

export default function InvoiceViewPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const invoiceId = params.id as string;
  
  const [invoice, setInvoice] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pdfBytes, setPdfBytes] = useState<ArrayBuffer | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);

  useEffect(() => {
    if (invoiceId) {
      loadInvoice();
    }
  }, [invoiceId]);

  const loadInvoice = async () => {
    try {
      setIsLoading(true);
      const apiResponse = await invoicesApi.getInvoiceById(invoiceId);
      
      // Extract actual data from the API response wrapper
      const data = apiResponse.data || apiResponse; // Handle both wrapped and unwrapped responses
      
      setInvoice(data);
      
      // The PDF is the one generator's (crm-api InvoicePdfRenderer), fetched —
      // never drawn here. See lib/document-pdf.ts.
      try {
        const { bytes } = await fetchDocumentPdf(invoiceId);
        // Rendered via pdf.js <canvas> (the shared PdfReader), NOT a blob:
        // <iframe> — the desktop webviews have no native PDF plugin.
        setPdfBytes(bytes);
      } catch (error) {
        console.error('Error loading PDF:', error);
        toast({
          title: "Warning",
          description: "Could not load the PDF preview",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Error loading invoice:', error);
      toast({
        title: "Error",
        description: "Failed to load invoice",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDownload = async () => {
    if (!invoice) return;

    try {
      await downloadDocumentPdf(invoiceId, {
        documentType: 'invoice',
        number: invoice.invoice_number,
        company: shareDocumentCompanyName(invoice),
      });
      toast({
        title: "Success",
        description: "Invoice downloaded successfully",
      });
    } catch (error: any) {
      console.error('Error downloading invoice:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to download invoice",
        variant: "destructive",
      });
    }
  };

  const handleEdit = () => {
    navigate(`/crm/invoices/generate?invoiceId=${invoiceId}`);
  };

  const handleDuplicate = () => {
    navigate(`/crm/invoices/generate?duplicateFrom=${invoiceId}`);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (!invoice) {
    return (
      <Box sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ pt: 3 }}>
            <Box sx={{ textAlign: "center", color: "text.secondary" }}>
              <Typography>Invoice not found</Typography>
              <BackButton sx={{ mt: 2 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4 }}>
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <BackButton />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    Invoice {invoice.invoice_number}
                  </Typography>
                  {invoice.send_status === 'scheduled' && (
                    <Chip size="small" variant="outlined" color="primary" icon={<Clock size={12} />} label="Scheduled" />
                  )}
                  {invoice.send_status === 'sent' && (
                    <Chip size="small" variant="outlined" color="success" label="Sent" />
                  )}
                  {invoice.invoice_schedule_id && (
                    <Chip size="small" variant="outlined" color="secondary" icon={<Repeat size={12} />} label="Recurring" />
                  )}
                  {invoice.auto_generated && (
                    <Chip size="small" variant="outlined" color="info" label="Auto-generated" />
                  )}
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="outlined" color="inherit" size="small" onClick={handleEdit} startIcon={<Edit size={16} />}>
                  Edit
                </Button>
                <Button variant="outlined" color="inherit" size="small" onClick={handleDuplicate} startIcon={<Copy size={16} />}>
                  Duplicate
                </Button>
                <Button variant="outlined" color="inherit" size="small" onClick={handleShare} startIcon={<Share2 size={16} />}>
                  Share
                </Button>
                <Button variant="outlined" color="inherit" size="small" onClick={() => setShowRecurringModal(true)} startIcon={<Repeat size={16} />}>
                  Set Up Recurring
                </Button>
                <Button variant="outlined" color="inherit" size="small" onClick={handleDownload} startIcon={<Download size={16} />}>
                  Download
                </Button>
              </Box>
            </Box>
          }
          subheader={invoice.customer_company_name ? invoice.customer_company_name : undefined}
        />
        <CardContent>
          {pdfBytes ? (
            <PdfReader
              bytes={pdfBytes}
              filename={shareDocumentPdfFilename('invoice', invoice.invoice_number || invoice.number, shareDocumentCompanyName(invoice))}
              containerTestId="invoice-pdf-preview"
              fallbackTestId="invoice-pdf-fallback"
              fallbackText="This invoice couldn’t be previewed inline. Use Download above to view it."
            />
          ) : (
            <Box sx={{ textAlign: "center", color: "text.secondary", py: 6 }}>
              <Typography sx={{ mb: 1, fontSize: "1.125rem", fontWeight: 500 }}>No PDF file available for this invoice</Typography>
              <Typography sx={{ fontSize: 14, mb: 3 }}>The invoice PDF may not have been generated yet.</Typography>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                <Button variant="contained" onClick={handleEdit} startIcon={<Edit size={16} />}>
                  Edit &amp; Generate Invoice
                </Button>
                <Button variant="outlined" color="inherit" onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Share Document Modal */}
      <ShareDocumentModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        documentId={invoiceId}
        documentType="invoice"
        documentNumber={invoice?.invoice_number}
      />

      {/* Create Recurring Schedule Modal */}
      <CreateRecurringScheduleModal
        isOpen={showRecurringModal}
        onClose={() => setShowRecurringModal(false)}
        onSuccess={() => {
          loadInvoice();
          toast({
            title: "Success",
            description: "Recurring schedule created",
          });
        }}
        invoice={{
          id: invoiceId,
          invoice_number: invoice?.invoice_number,
          total_amount: invoice?.total_amount,
          customer_id: invoice?.customer?.id || invoice?.customer_id,
          customer_name: invoice?.customer?.full_name || invoice?.customer_name,
          customer_email: invoice?.customer?.email,
        }}
      />
    </Box>
  );
}

